declare global {
  interface Window {
    onYouTubeIframeAPIReady: (() => void) | undefined;
    YT: typeof YT;
  }
}

declare namespace YT {
  enum PlayerState {
    UNSTARTED = -1,
    ENDED = 0,
    PLAYING = 1,
    PAUSED = 2,
    BUFFERING = 3,
    CUED = 5,
  }

  interface PlayerEvent {
    data: number;
    target: Player;
  }

  interface VideoData {
    title: string;
  }

  class Player {
    constructor(
      elementId: string | HTMLIFrameElement,
      options?: { events?: { onStateChange?: (event: PlayerEvent) => void } },
    );
    getVideoData(): VideoData;
    getVideoUrl(): string;
    getCurrentTime(): number;
    getDuration(): number;
  }
}

export interface YouTubeVideoEvent {
  event: string;
  video_action: string;
  video_provider: string;
  video_title: string;
  video_url: string;
  video_percent: number;
}

const STATE_ACTIONS: Record<number, string> = {
  1: 'play',
  2: 'pause',
  0: 'complete',
};

function loadYouTubeAPI(): void {
  if (document.querySelector('script[src*="youtube.com/iframe_api"]')) return;

  const tag = document.createElement('script');
  tag.src = 'https://www.youtube.com/iframe_api';
  document.head.appendChild(tag);
}

function initPlayers(): void {
  const iframes =
    document.querySelectorAll<HTMLIFrameElement>('iframe[id^="yt-player-"]');

  iframes.forEach((iframe) => {
    new window.YT.Player(iframe, {
      events: {
        onStateChange(event: YT.PlayerEvent): void {
          const action = STATE_ACTIONS[event.data];
          if (!action) return;

          const videoEvent: YouTubeVideoEvent = {
            event: 'video_interaction',
            video_action: action,
            video_provider: 'youtube',
            video_title: event.target.getVideoData().title,
            video_url: event.target.getVideoUrl(),
            video_percent: Math.round(
              (event.target.getCurrentTime() / event.target.getDuration()) *
                100,
            ),
          };

          const dataLayer = window.dataLayer || [];
          dataLayer.push(videoEvent as never);
        },
      },
    });
  });
}

export function initYouTubeTracking(): void {
  function setup(): void {
    const iframes =
      document.querySelectorAll<HTMLIFrameElement>('iframe[id^="yt-player-"]');

    if (iframes.length === 0) return;

    loadYouTubeAPI();

    const existingCallback = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      if (existingCallback) existingCallback();
      initPlayers();
    };

    // If the API was already loaded (e.g. navigated via AJAX)
    if (window.YT?.Player) {
      initPlayers();
    }
  }

  // Wait for the DOM to be fully parsed before searching for iframes
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
}